import React from 'react';
import {
  Box,
  VStack,
  Flex,
  Text,
  Icon,
  Collapse,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Role } from '../../types';
import { RoleNode, DEPARTMENT_COLORS } from './RoleNode';
import { FaGripVertical } from 'react-icons/fa';

interface DepartmentsViewProps {
  departments: Role[];
  allRoles: Role[];
  onRoleClick?: (role: Role) => void;
  onAddChildClick?: (parentRole: Role) => void;
  isSuperUser?: boolean;
}

const DepartmentRow = ({
  department,
  allRoles,
  onRoleClick,
  onAddChildClick,
  isSuperUser,
  index,
}: {
  department: Role;
  allRoles: Role[];
  onRoleClick?: (role: Role) => void;
  onAddChildClick?: (parentRole: Role) => void;
  isSuperUser?: boolean;
  index: number;
}) => {
  const { isOpen, onToggle } = useDisclosure();
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Use department's matching color index from RoleNode
  const stripeColor = DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length];

  return (
    <Box w="full" borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={bg} overflow="hidden" shadow="sm">
      <Flex
        p={4}
        align="center"
        cursor="pointer"
        _hover={{ bg: hoverBg }}
        onClick={onToggle}
        position="relative"
      >
        <Box position="absolute" left="0" top="0" bottom="0" w="4px" bg={stripeColor} />
        
        <Icon as={FaGripVertical} color="gray.300" mr={4} ml={2} />
        
        <Box flex="1">
          <Flex align="baseline" gap={2}>
            <Text fontWeight="bold" fontSize="lg">{department.title}</Text>
            {department.ownerName && (
              <Text color="gray.500" fontSize="md">({department.ownerName})</Text>
            )}
          </Flex>
          <Flex mt={1} gap={4} fontSize="sm">
            <Flex align="center" gap={1}>
              <Box w={8} h="4px" bg={stripeColor} borderRadius="sm" />
              <Text color="gray.500">Filled Seats</Text>
            </Flex>
            <Text color="red.500" display="flex" align="center" gap={1}>
              <Icon as={ChevronUpIcon} /> 0 GWC
            </Text>
          </Flex>
        </Box>
        
        <Icon as={isOpen ? ChevronDownIcon : ChevronRightIcon} color="gray.400" w={6} h={6} />
      </Flex>
      
      <Collapse in={isOpen}>
        <Box p={8} bg="gray.50" borderTopWidth="1px" borderColor={borderColor} overflowX="auto">
          <Flex justify="center" minW="max-content">
            {/* Render the department as the root of this sub-tree */}
            <RoleNode
              role={department}
              allRoles={allRoles}
              onRoleClick={onRoleClick}
              onAddChildClick={onAddChildClick}
              isSuperUser={isSuperUser}
              defaultExpanded={true}
              inheritedColor={stripeColor}
            />
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

// Mocking ChevronUpIcon because it was missed in imports above
// Actually I'll just remove the chevron up icon from GWC and replace with basic triangle
const TriangleUpIcon = (props: any) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path fill="currentColor" d="M1 21h22L12 2 1 21z" />
  </Icon>
);

export const DepartmentsView: React.FC<DepartmentsViewProps> = ({
  departments,
  allRoles,
  onRoleClick,
  onAddChildClick,
  isSuperUser,
}) => {
  return (
    <VStack spacing={4} align="stretch" w="full" mx="auto">
      {departments.map((dept, index) => (
        <DepartmentRow
          key={dept.id}
          department={dept}
          allRoles={allRoles}
          onRoleClick={onRoleClick}
          onAddChildClick={onAddChildClick}
          isSuperUser={isSuperUser}
          index={index}
        />
      ))}
      
      {departments.length === 0 && (
        <Box textAlign="center" py={12} color="gray.500">
          No departments defined yet.
        </Box>
      )}
    </VStack>
  );
};
